import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Calendar as CalendarIcon,
  BarChart3,
  CalendarCheck,
  Images,
  MessageSquare,
  FolderTree,
  Package,
  HelpCircle,
  Settings as SettingsIcon,
  TrendingUp,
  Clock,
  FileSpreadsheet,
} from 'lucide-react';
import {
  subscribeToGallery,
  subscribeToTestimonials,
} from '../services/firebase';
import { getBookings, subscribeToBookings } from '../services/firestore';
import BookingCalendar from '../components/admin/BookingCalendar';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import RecentActivity from '../components/admin/RecentActivity';
import UpcomingBookings from '../components/admin/UpcomingBookings';
import ScheduleManagement from '../components/admin/ScheduleManagement';
import ReportsAndExports from '../components/admin/ReportsAndExports';
import AdminTabs from '../components/admin/AdminTabs';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({
    galleryCount: 0,
    testimonialCount: 0,
    pendingTestimonials: 0,
    bookingCount: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
  });

  // Load active tab from localStorage
  useEffect(() => {
    const savedTab = localStorage.getItem('dashboardActiveTab');
    if (savedTab && ['overview', 'calendar', 'analytics', 'schedule', 'reports'].includes(savedTab)) {
      setActiveTab(savedTab);
    }
  }, []);

  // Save active tab to localStorage
  useEffect(() => {
    localStorage.setItem('dashboardActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    loadData();

    // Subscribe to bookings for real-time updates
    const unsubscribeBookings = subscribeToBookings((data) => {
      setBookings(data);
      setStats((prev) => ({
        ...prev,
        bookingCount: data.length,
        pendingBookings: data.filter((b) => b.status === 'PENDING_DEPOSIT').length,
        confirmedBookings: data.filter((b) => b.status === 'CONFIRMED').length,
      }));
    });

    return () => {
      if (unsubscribeBookings) unsubscribeBookings();
    };
  }, []);

  const loadData = async () => {
    try {
      // Load gallery count
      const unsubscribeGallery = subscribeToGallery((data) => {
        setStats((prev) => ({ ...prev, galleryCount: data.length }));
      });

      // Load testimonial stats
      const unsubscribeTestimonials = subscribeToTestimonials(
        (data) => {
          setStats((prev) => ({
            ...prev,
            testimonialCount: data.length,
            pendingTestimonials: data.filter((t) => !t.approved).length,
          }));
        },
        true
      );

      setLoading(false);

      return () => {
        unsubscribeGallery();
        unsubscribeTestimonials();
      };
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  // Handle booking click from calendar
  const handleBookingClick = (booking) => {
    navigate(`/admin/bookings`);
    // The booking details will be shown in the bookings page
    // You could also store the selected booking ID to auto-open details
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF7A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#F7F5F2]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: LayoutDashboard },
    { id: 'calendar', name: 'Calendar', icon: CalendarIcon },
    { id: 'schedule', name: 'Schedule', icon: Clock },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
    { id: 'reports', name: 'Reports', icon: FileSpreadsheet },
  ];

  const quickLinks = [
    {
      title: 'Manajemen Booking',
      description: 'Kelola booking dan pembayaran',
      icon: CalendarCheck,
      path: '/admin/bookings',
      color: 'from-blue-600 to-blue-700',
      count: stats.bookingCount,
    },
    {
      title: 'Gallery',
      description: 'Kelola foto galeri',
      icon: Images,
      path: '/admin/gallery',
      color: 'from-purple-600 to-purple-700',
      count: stats.galleryCount,
    },
    {
      title: 'Testimoni',
      description: 'Kelola testimoni klien',
      icon: MessageSquare,
      path: '/admin/testimonials',
      color: 'from-green-600 to-green-700',
      count: stats.pendingTestimonials,
      badge: `${stats.pendingTestimonials} pending`,
    },
    {
      title: 'Kategori',
      description: 'Kelola kategori layanan',
      icon: FolderTree,
      path: '/admin/categories',
      color: 'from-orange-600 to-orange-700',
    },
    {
      title: 'Layanan',
      description: 'Kelola paket & add-ons',
      icon: Package,
      path: '/admin/packages',
      color: 'from-pink-600 to-pink-700',
    },
    {
      title: 'FAQ',
      description: 'Kelola pertanyaan umum',
      icon: HelpCircle,
      path: '/admin/faq',
      color: 'from-teal-600 to-teal-700',
    },
    {
      title: 'Pengaturan',
      description: 'Kelola kontak, sosial & syarat',
      icon: SettingsIcon,
      path: '/admin/settings',
      color: 'from-gray-600 to-gray-700',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#F7F5F2] mb-2">
          Selamat Datang, {user?.displayName?.split(' ')[0] || 'Admin'}! 👋
        </h1>
        <p className="text-[#F7F5F2]/70">
          Berikut adalah ringkasan aktivitas booking dan galeri Anda hari ini.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#D4AF7A]/20">
        <AdminTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-[#F7F5F2]/70">Total Booking</p>
                    <p className="text-3xl font-bold text-[#F7F5F2] mt-1">
                      {stats.bookingCount}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <CalendarCheck className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-[#F7F5F2]/70">{stats.confirmedBookings} dikonfirmasi</span>
                </div>
              </div>

              <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-[#F7F5F2]/70">Gallery</p>
                    <p className="text-3xl font-bold text-[#F7F5F2] mt-1">
                      {stats.galleryCount}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Images className="w-6 h-6 text-purple-400" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-[#F7F5F2]/70">Total foto galeri</span>
                </div>
              </div>

              <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-[#F7F5F2]/70">Testimoni</p>
                    <p className="text-3xl font-bold text-[#F7F5F2] mt-1">
                      {stats.testimonialCount}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-green-400" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-yellow-400">{stats.pendingTestimonials} pending</span>
                  <span className="text-[#F7F5F2]/70">•</span>
                  <span className="text-[#F7F5F2]/70">
                    {stats.testimonialCount - stats.pendingTestimonials} approved
                  </span>
                </div>
              </div>

              <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-[#F7F5F2]/70">Pending Bookings</p>
                    <p className="text-3xl font-bold text-yellow-400 mt-1">
                      {stats.pendingBookings}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <LayoutDashboard className="w-6 h-6 text-yellow-400" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#F7F5F2]/70">Menunggu deposit</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h2 className="text-xl font-bold text-[#F7F5F2] mb-4">Quick Access</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {quickLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <button
                      key={link.path}
                      onClick={() => navigate(link.path)}
                      className="group relative bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6 text-left hover:border-[#D4AF7A]/40 transition-all"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 bg-gradient-to-br ${link.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-[#F7F5F2] mb-1 group-hover:text-[#D4AF7A] transition-colors">
                            {link.title}
                          </h3>
                          <p className="text-sm text-[#F7F5F2]/60 mb-2">{link.description}</p>
                          {link.count !== undefined && (
                            <span className="text-xs font-medium text-[#D4AF7A]">
                              {link.badge || `${link.count} items`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                          className="w-5 h-5 text-[#D4AF7A]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Pending Actions Alert */}
            {(stats.pendingBookings > 0 || stats.pendingTestimonials > 0) && (
              <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LayoutDashboard className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                      Action Required
                    </h3>
                    <p className="text-[#F7F5F2]/80 mb-3">
                      You have pending items that need your attention:
                    </p>
                    <ul className="space-y-2 text-sm">
                      {stats.pendingBookings > 0 && (
                        <li className="flex items-center gap-2 text-[#F7F5F2]/70">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                          <span>
                            <strong className="text-[#F7F5F2]">{stats.pendingBookings} bookings</strong>{' '}
                            waiting for deposit verification
                          </span>
                          <button
                            onClick={() => navigate('/admin/bookings')}
                            className="ml-auto px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                          >
                            View
                          </button>
                        </li>
                      )}
                      {stats.pendingTestimonials > 0 && (
                        <li className="flex items-center gap-2 text-[#F7F5F2]/70">
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                          <span>
                            <strong className="text-[#F7F5F2]">{stats.pendingTestimonials} testimonials</strong>{' '}
                            waiting for approval
                          </span>
                          <button
                            onClick={() => navigate('/admin/testimonials')}
                            className="ml-auto px-3 py-1 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors"
                          >
                            Review
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Activity & Upcoming Bookings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RecentActivity limit={5} />
              <UpcomingBookings limit={5} />
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
            <BookingCalendar bookings={bookings} onBookingClick={handleBookingClick} />
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
            <ScheduleManagement />
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
            <AnalyticsDashboard bookings={bookings} />
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
            <ReportsAndExports />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
